<template>
  <div :class="`custom-select-wrapper ${id} ${isDisabled ? 'disabled' : ''}`">
    <label v-if="label" :class="['input-label', { 'visually-hidden': hideLabel }]" :for="id">
      <span>{{ label }}</span>
    </label>

    <div
      ref="selectRef"
      class="custom-select"
      v-bind="attrs"
      @click="handleSelectClick"
      @keydown.stop="handleKeydown"
      tabindex="0"
    >
      <!-- Optional icon slot (e.g., flag) -->
      <span v-if="$slots.icon" class="select-icon">
        <slot name="icon"></slot>
      </span>

      <!-- Main input -->
      <input
        ref="inputRef"
        :id="id"
        class="selected-input"
        :value="selectedLabel"
        :readonly="!showDropdown || isMultiple"
        :placeholder="t('ui.select.defaultOption')"
        :aria-label="inputAriaLabel"
        @focus="openDropdown"
      />

      <!-- Dropdown arrow icon -->
      <DropdownIcon class="icon-dropdown" color="black" />

      <!-- Dropdown / Drop‑up list -->
      <ul
        v-if="showDropdown"
        class="option-list"
        :class="[placement, { 'keyboard-nav': isKeyboardNav }]"
        ref="optionListRef"
      >
        <li
          v-if="isMultiple && filteredOptions.length"
          class="option-item"
          @click.stop="toggleSelectAll"
          @mousemove="onMouseMove(-1)"
        >
          <CustomCheckbox
            :id="`${id}-select-all`"
            :label="t('ui.select.selectAll')"
            :defaultChecked="allVisibleSelected"
            @emit:change="toggleSelectAll"
          />
        </li>
        <li
          v-for="(option, index) in filteredOptions"
          :key="option.value"
          data-test-id="option-item"
          :class="['option-item', { focused: index === focusedIndex, selected: option.value === selectedValue }]"
          @click.stop="selectOption(option)"
          @mousemove="onMouseMove(index)"
        >
          <template v-if="isMultiple">
            <CustomCheckbox
              :id="`${id}-${option.value}`"
              :label="option.label"
              :defaultChecked="selectedValues.includes(option.value)"
              @emit:change="() => selectOption(option)"
            />
          </template>
          <template v-else>
            {{ option.label }}
          </template>
        </li>
        <li v-if="filteredOptions.length === 0" class="option-item disabled">
          {{ t('ui.select.noResults') }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useAttrs, watch, watchEffect } from 'vue'

import CustomCheckbox from '@/features/ui/components/checkbox/custom-checkbox.vue'
import DropdownIcon from '@/features/ui/components/icons/dropdown-icon.vue'
import { useI18n } from '@/lib/i18n'

type PlacementType = 'bottom' | 'top'

const props = defineProps<{
  // label peut être null : un groupe sans nom (mentorship_group.name est nullable)
  options: { label: string | null; value: string }[]
  id: string
  value?: string | string[] | null
  label?: string
  hideLabel?: boolean
  ariaLabel?: string
  isDisabled?: boolean
  placement?: PlacementType
  isMultiple?: boolean
}>()

const emit = defineEmits(['emit:change'])
const attrs = useAttrs()
const { t } = useI18n()

/* ------------------------- reactive state ------------------------- */
const selectedValue = ref(!props.isMultiple ? (props.value as string) || '' : '')
const selectedValues = ref<string[]>(Array.isArray(props.value) ? props.value : [])
const searchQuery = ref('')
const showDropdown = ref(false)
const inputRef = ref<HTMLInputElement | null>(null)
const optionListRef = ref<HTMLUListElement | null>(null)
const selectRef = ref<HTMLElement | null>(null)
const focusedIndex = ref(-1)
const isKeyboardNav = ref(false)

const placement = computed<PlacementType>(() => props.placement ?? 'bottom')
const inputAriaLabel = computed(() => (props.label ? undefined : props.ariaLabel || t('ui.select.defaultOption')))

/* --------------------------- computed ---------------------------- */
const selectedLabel = computed(() => {
  if (props.isMultiple) {
    return props.options
      .filter((o) => selectedValues.value.includes(o.value))
      .map((o) => o.label)
      .join(', ')
  } else {
    return props.options.find((o) => o.value === selectedValue.value)?.label || ''
  }
})

const filteredOptions = computed(() =>
  props.options.filter((o) => (o.label ?? '').toLowerCase().includes(searchQuery.value.toLowerCase())),
)

const allVisibleSelected = computed(() =>
  props.isMultiple && filteredOptions.value.length
    ? filteredOptions.value.every((o) => selectedValues.value.includes(o.value))
    : false,
)

/* ------------- sync external value & displayed label ------------- */
watch(
  () => props.value,
  (val) => {
    if (props.isMultiple) {
      selectedValues.value = Array.isArray(val) ? val : []
    } else {
      selectedValue.value = typeof val === 'string' ? val : ''
    }
  },
)

watchEffect(() => {
  if (!showDropdown.value) searchQuery.value = selectedLabel.value
})

/* ------------------ focus & list scroll management --------------- */
watch(
  () => [showDropdown.value, filteredOptions.value.length],
  () => {
    focusedIndex.value = showDropdown.value && filteredOptions.value.length ? 0 : -1
  },
)

watch(focusedIndex, () => {
  nextTick(() => {
    const el = optionListRef.value?.children[focusedIndex.value] as HTMLElement | undefined
    el?.scrollIntoView({ block: 'nearest' })
  })
})

/* ---------------------- keyboard navigation ---------------------- */
const keyToDir = (key: string) => {
  if (key === 'ArrowDown') return placement.value === 'top' ? -1 : 1
  if (key === 'ArrowUp') return placement.value === 'top' ? 1 : -1
  return 0
}

const handleKeydown = (e: KeyboardEvent) => {
  const { key } = e
  if (!showDropdown.value && (key === 'ArrowDown' || key === 'ArrowUp')) {
    openDropdown()
    e.preventDefault()
    return
  }
  if (!showDropdown.value) return

  const dir = keyToDir(key)
  if (dir) {
    focusedIndex.value = (focusedIndex.value + dir + filteredOptions.value.length) % filteredOptions.value.length
    isKeyboardNav.value = true
    e.preventDefault()
  } else if (key === 'Enter') {
    const option = filteredOptions.value[focusedIndex.value]
    if (option) selectOption(option)
    e.preventDefault()
  } else if (key === 'Escape') {
    closeDropdown()
    e.preventDefault()
  }
}

/* --------------------------- mouse move -------------------------- */
const onMouseMove = (i: number) => {
  isKeyboardNav.value = false
  focusedIndex.value = i
}

/* ------------------------- open / close -------------------------- */
const openDropdown = () => {
  if (props.isDisabled || showDropdown.value) return
  showDropdown.value = true
  searchQuery.value = ''
  nextTick(() => {
    inputRef.value?.focus()
    if (placement.value === 'top' && optionListRef.value) {
      optionListRef.value.scrollTop = optionListRef.value.scrollHeight
    }
  })
}

/* ------------------- nouveau gestionnaire ------------------- */
const handleSelectClick = (e: MouseEvent) => {
  const clickedInput = inputRef.value?.contains(e.target as Node)

  if (showDropdown.value) {
    // La liste est déjà ouverte → on ferme si l'input n'a pas été cliqué
    if (!clickedInput) closeDropdown()
  } else {
    // Liste fermée → on l’ouvre
    openDropdown()
  }
}

const closeDropdown = () => {
  showDropdown.value = false
  isKeyboardNav.value = false
}

const toggleSelectAll = () => {
  if (!props.isMultiple) return
  if (allVisibleSelected.value) {
    selectedValues.value = selectedValues.value.filter((val) => !filteredOptions.value.some((o) => o.value === val))
  } else {
    const toAdd = filteredOptions.value.map((o) => o.value).filter((v) => !selectedValues.value.includes(v))
    selectedValues.value = [...selectedValues.value, ...toAdd]
  }
  emit('emit:change', [...selectedValues.value])
}

const selectOption = (o: { label: string; value: string }) => {
  if (props.isMultiple) {
    const index = selectedValues.value.indexOf(o.value)
    if (index > -1) {
      selectedValues.value.splice(index, 1)
    } else {
      selectedValues.value.push(o.value)
    }
    emit('emit:change', [...selectedValues.value])
  } else {
    selectedValue.value = o.value
    emit('emit:change', o.value)
    closeDropdown()
  }
}

/* ------------------------- outside click ------------------------- */
const handleClickOutside = (e: MouseEvent) => {
  if (selectRef.value && !selectRef.value.contains(e.target as Node)) {
    closeDropdown()
  }
}

onMounted(() => document.addEventListener('click', handleClickOutside))
onBeforeUnmount(() => document.removeEventListener('click', handleClickOutside))

// Expose clear() method to parent
function clear() {
  if (props.isMultiple) {
    selectedValues.value = []
    emit('emit:change', [])
  } else {
    selectedValue.value = ''
    emit('emit:change', '')
  }
}
defineExpose({ clear })
</script>

<style lang="scss">
@import '@/styles/utils';
@import '@/features/ui/components/input/custom-input.scss';

.custom-select-wrapper {
  @extend .input-wrapper;

  &.disabled {
    pointer-events: none;
    opacity: 0.5;
    user-select: none;
  }

  .custom-select {
    position: relative;
    display: flex;
    align-items: center;
    gap: $s * 1.5;
    padding: $s * 1.25 $s * 2;
    color: rgb($black);
    border: 2px solid rgba($black, $opacity-10);
    border-radius: $s * 0.5;
    font-size: $s * 1.75;
    cursor: pointer;
    background: white;
    outline: none;

    &:focus-visible {
      box-shadow: 0 0 0 2px rgba($primary, 0.5);
    }

    /* Icon (slot) */
    .select-icon {
      display: flex;
      align-items: center;
      pointer-events: none; // keep clicks on input

      img,
      svg {
        width: $s * 2.5; // ≈20px if $s=8
        height: $s * 2.5;
      }
    }

    .selected-input {
      flex: 1;
      border: none;
      outline: none;
      background: transparent;
      font-size: inherit;
      padding: 0;
      &::placeholder {
        opacity: 0.2;
      }
      &[readonly] {
        cursor: pointer;
      }
    }

    .icon-dropdown {
      margin-left: auto;
      width: $s * 2;
      pointer-events: none;
    }

    /* ---------------- List ---------------- */
    .option-list {
      position: absolute;
      left: 0;
      z-index: 10;
      width: 100%;
      background: white;
      border: 1px solid rgba($black, $opacity-10);
      border-radius: $s * 0.5;
      max-height: 170px;
      overflow-y: auto;
      list-style: none;
      padding: $s;

      &.bottom {
        top: 100%;
        margin-top: $s * 0.5;
      }

      &.top {
        bottom: 100%;
        margin-bottom: $s * 0.5;
        display: flex;
        flex-direction: column-reverse;
      }

      .option-item {
        padding: $s * 1;
        cursor: pointer;
        &:hover,
        &.focused {
          background: rgba($black, 0.05);
        }
        &.selected {
          background-color: rgb($primary, $opacity-05);
          color: rgb($primary);
        }
      }

      &.keyboard-nav .option-item:hover:not(.focused) {
        background: transparent;
      }

      .option-item.disabled {
        color: gray;
        pointer-events: none;
      }
    }
  }
}
</style>
